import { cn } from "@/lib/utils";
import { Check, Sigma } from "lucide-react";
import { forwardRef, ReactNode } from "react";

export type TagListItemVariant = "default" | "hover" | "selected";

export interface TagListItemProps {
  /** Additional class names */
  className?: string;
  /** Children components */
  children: ReactNode;
}

interface TagListItemBodyProps {
  /** The current variant/state of the item */
  variant?: TagListItemVariant;
  /** Whether the item is selected (controlled) */
  isSelected?: boolean;
  /** Click handler */
  onClick?: () => void;
  /** Mouse enter handler */
  onMouseEnter?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** Mouse leave handler */
  onMouseLeave?: () => void;
  /** Additional class names */
  className?: string;
  /** Whether the item is disabled */
  disabled?: boolean;
  /** Children components */
  children: ReactNode;
}

interface TagListItemHeaderProps {
  /** Children components */
  children: ReactNode;
  /** Additional class names */
  className?: string;
}

interface SigmaChipProps {
  className?: string;
}

interface LabelProps {
  children: ReactNode;
  className?: string;
}

interface BadgeContainerProps {
  children?: ReactNode;
  className?: string;
}

/**
 * Header - Header section above the body
 */
const Header = ({ children, className }: TagListItemHeaderProps) => {
  return (
    <div className={cn("w-full flex flex-row items-center gap-1", className)}>
      {children}
    </div>
  );
};

/**
 * Body - Main clickable body with check indicator
 */
const Body = forwardRef<HTMLButtonElement, TagListItemBodyProps>(
  (
    {
      variant = "default",
      isSelected,
      onClick,
      onMouseEnter,
      onMouseLeave,
      className,
      disabled = false,
      children,
    },
    ref,
  ) => {
    // Determine if selected based on prop or variant
    const selected = isSelected ?? variant === "selected";

    return (
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        disabled={disabled}
        className={cn(
          "group flex h-[38px] w-full items-center gap-[6px] rounded-[8px] px-[12px] py-[8px] transition-colors",
          "bg-white hover:bg-blue-50 active:bg-neutral-100",
          selected && "bg-neutral-100",
          className,
        )}
      >
        <div
          className={cn(
            "flex h-[14px] w-[14px] shrink-0 items-center justify-center rounded-full transition-all",
            selected ? "bg-[#171717]" : "bg-[#171717] opacity-20",
          )}
        >
          <Check className="h-[8px] w-[8px] text-white" />
        </div>

        {children}
      </button>
    );
  },
);

Body.displayName = "TagListItem.Body";

/**
 * SigmaChip - Orange chip with Sigma icon
 */
const SigmaChip = ({ className }: SigmaChipProps) => {
  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center rounded-[2px] bg-orange-500 p-[2px]",
        className,
      )}
    >
      <Sigma className="h-[8px] w-[8px] text-white" />
    </div>
  );
};

/**
 * Label - Text content for the tag list item
 */
const Label = ({ children, className }: LabelProps) => {
  return (
    <p
      className={cn(
        "min-w-0 flex-1 truncate text-left text-[#171717] body-2-normal-medium",
        className,
      )}
    >
      {children}
    </p>
  );
};

/**
 * BadgeContainer - Container for badges (e.g., ExtendedChip)
 */
const BadgeContainer = ({ children, className }: BadgeContainerProps) => {
  return (
    <div className={cn("flex flex-row items-center gap-1", className)}>
      {children}
    </div>
  );
};

/**
 * TagListItem component based on Figma design (Compound Component Pattern)
 * Displays a selectable list item with composable child components
 *
 * @example
 * ```tsx
 * <TagListItem>
 *   <TagListItem.Header>
 *     Header Content
 *   </TagListItem.Header>
 *   <TagListItem.Body isSelected onClick={handleClick}>
 *     <TagListItem.SigmaChip />
 *     <TagListItem.Label>Label Text</TagListItem.Label>
 *     <TagListItem.BadgeContainer>
 *       <ExtendedChip />
 *     </TagListItem.BadgeContainer>
 *   </TagListItem.Body>
 * </TagListItem>
 * ```
 */
const TagListItemRoot = ({ children, className }: TagListItemProps) => {
  return (
    <div className={cn("flex flex-col w-full gap-0.5", className)}>
      {children}
    </div>
  );
};

export const TagListItem = Object.assign(TagListItemRoot, {
  Header,
  Body,
  SigmaChip,
  Label,
  BadgeContainer,
  displayName: "TagListItem",
});

export default TagListItem;
